import React, { useState } from 'react';
import { 
  Car, User, Shield, Mail, Phone, CheckCircle, 
  ChevronRight, ChevronLeft, Send, AlertCircle,
  BadgeCheck, Users, Heart, HelpCircle
} from 'lucide-react';
import axios from 'axios';

const BastaLaTargaForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Step 1: Qualificazione
    qualificazione: '',
    
    // Step 2: Dati Veicolo e Intestatario
    targa: '',
    cognomeNome: '',
    indirizzoResidenza: '',
    veicoloEstero: 'no',
    codiceFiscale: '',
    professione: '',
    titoloStudio: '',
    
    // Step 3: Garanzie
    massimaleRCA: '',
    garanzie: [],
    
    // Step 4: Contatti e Consensi
    email: '',
    cellulare: '',
    consensoPrivacy: false,
    consensoMarketing: false
  });

  const totalSteps = 4;

  const qualificazioniOptions = [
    { value: 'socio_dipendente', label: 'Socio/Dipendente Coop o ETS', icon: Users, hasAgevolazione: true },
    { value: 'volontario', label: 'Volontario ETS', icon: Heart, hasAgevolazione: true },
    { value: 'familiare_disabilita', label: 'Familiare persona con disabilità', icon: Heart, hasAgevolazione: true },
    { value: 'nessuna', label: 'Nessuna delle precedenti', icon: HelpCircle, hasAgevolazione: false }
  ];

  const massimaliOptions = [
    { value: 'minimi', label: 'Massimali Minimi di Legge' },
    { value: '10_milioni', label: '€ 10.000.000' },
    { value: 'oltre_10_milioni', label: 'Oltre € 10.000.000' }
  ];

  const garanzieOptions = [
    'Assistenza Stradale',
    'Tutela Legale Penale (sulla Targa)',
    'Tutela Legale sulla Patente (contratto Separato emesso da SLP Ass.ni)',
    'Infortuni Conducente',
    'Cristalli',
    'Incendio e Furto',
    'Atti Vandalici',
    'Collisione',
    'Collisione con Animali',
    'Kasko',
    'Eventi Atmosferici/Sociopolitici'
  ];

  const hasAgevolazione = formData.qualificazione && 
    qualificazioniOptions.find(q => q.value === formData.qualificazione)?.hasAgevolazione;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'garanzie') {
        setFormData(prev => ({
          ...prev,
          garanzie: checked 
            ? [...prev.garanzie, value]
            : prev.garanzie.filter(item => item !== value)
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else if (name === 'targa') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch(step) {
      case 1:
        if (!formData.qualificazione) newErrors.qualificazione = 'Seleziona una opzione';
        break;
      case 2:
        if (!formData.targa) newErrors.targa = 'Targa obbligatoria';
        if (!formData.cognomeNome) newErrors.cognomeNome = 'Campo obbligatorio';
        if (!formData.indirizzoResidenza) newErrors.indirizzoResidenza = 'Campo obbligatorio';
        break;
      case 3:
        if (!formData.massimaleRCA) newErrors.massimaleRCA = 'Seleziona un massimale';
        break;
      case 4:
        if (!formData.email) newErrors.email = 'Email obbligatoria';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email non valida';
        if (!formData.cellulare) newErrors.cellulare = 'Cellulare obbligatorio';
        if (!formData.consensoPrivacy) newErrors.consensoPrivacy = 'Consenso obbligatorio per procedere';
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/rca-quotazione`,
        formData,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status === 'success') {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Errore invio:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIcons = {
    1: Users,
    2: Car,
    3: Shield,
    4: Mail
  };

  const stepTitles = {
    1: 'Qualificazione',
    2: 'Dati Veicolo',
    3: 'Garanzie',
    4: 'Contatti'
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        {[1, 2, 3, 4].map((step) => {
          const Icon = stepIcons[step];
          const isActive = currentStep === step;
          const isCompleted = currentStep > step;
          
          return (
            <div key={step} className="flex-1 flex items-center">
              <div className="flex flex-col items-center w-full">
                <div 
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive 
                      ? 'bg-[#E74C3C] text-white shadow-lg scale-110' 
                      : isCompleted 
                        ? 'bg-[#5FB76E] text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {isCompleted ? <CheckCircle size={20} /> : <Icon size={20} />}
                </div>
                <span className={`text-xs mt-2 text-center hidden sm:block ${
                  isActive ? 'text-[#E74C3C] font-semibold' : 'text-gray-500'
                }`}>
                  {stepTitles[step]}
                </span>
              </div>
              {step < 4 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  currentStep > step ? 'bg-[#5FB76E]' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#E74C3C]/10 rounded-lg flex items-center justify-center">
          <Users className="text-[#E74C3C]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#E74C3C]">Qualificazione e Vantaggio</h3>
      </div>
      
      <p className="text-gray-600 mb-6">Fai parte del mondo del Terzo Settore o di una Cooperativa?</p>
      
      <div className="space-y-3">
        {qualificazioniOptions.map(option => {
          const Icon = option.icon;
          const isSelected = formData.qualificazione === option.value;
          return (
            <label 
              key={option.value} 
              className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                isSelected
                  ? 'border-[#E74C3C] bg-[#E74C3C]/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="qualificazione"
                value={option.value}
                checked={isSelected}
                onChange={handleInputChange}
                className="sr-only"
              />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${
                isSelected ? 'bg-[#E74C3C] text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                <Icon size={20} />
              </div>
              <span className={`flex-1 font-medium ${isSelected ? 'text-[#E74C3C]' : 'text-gray-700'}`}>
                {option.label}
              </span>
              {isSelected && option.hasAgevolazione && (
                <span className="bg-[#5FB76E] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center">
                  <BadgeCheck size={14} className="mr-1" />
                  Agevolazioni attivate
                </span>
              )}
            </label>
          );
        })}
      </div>
      {errors.qualificazione && <p className="text-red-500 text-sm mt-2">{errors.qualificazione}</p>}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#3AAFA9]/10 rounded-lg flex items-center justify-center">
          <Car className="text-[#3AAFA9]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#3AAFA9]">Dati Veicolo e Intestatario</h3>
      </div>
      
      {hasAgevolazione && (
        <div className="bg-[#5FB76E]/10 border border-[#5FB76E]/30 rounded-lg p-4 mb-6 flex items-center">
          <BadgeCheck className="text-[#5FB76E] mr-3" size={24} />
          <span className="text-[#5FB76E] font-medium">Agevolazioni attivate per il tuo profilo</span>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Targa <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="targa"
            value={formData.targa}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.targa ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent transition-all uppercase font-mono text-lg tracking-wider`}
            placeholder="AA000AA"
            maxLength={7}
          />
          {errors.targa && <p className="text-red-500 text-sm mt-1">{errors.targa}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Veicolo immatricolato all'estero?
          </label>
          <div className="flex space-x-4">
            <label className={`flex-1 p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${
              formData.veicoloEstero === 'no' ? 'border-[#3AAFA9] bg-[#3AAFA9]/5' : 'border-gray-200'
            }`}>
              <input type="radio" name="veicoloEstero" value="no" checked={formData.veicoloEstero === 'no'} onChange={handleInputChange} className="sr-only" />
              <span className={formData.veicoloEstero === 'no' ? 'font-semibold text-[#3AAFA9]' : 'text-gray-600'}>No</span>
            </label>
            <label className={`flex-1 p-3 rounded-lg border-2 cursor-pointer text-center transition-all ${
              formData.veicoloEstero === 'si' ? 'border-[#3AAFA9] bg-[#3AAFA9]/5' : 'border-gray-200'
            }`}>
              <input type="radio" name="veicoloEstero" value="si" checked={formData.veicoloEstero === 'si'} onChange={handleInputChange} className="sr-only" />
              <span className={formData.veicoloEstero === 'si' ? 'font-semibold text-[#3AAFA9]' : 'text-gray-600'}>Si</span>
            </label>
          </div>
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cognome e Nome <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              name="cognomeNome"
              value={formData.cognomeNome}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.cognomeNome ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent transition-all`}
              placeholder="Rossi Mario"
            />
          </div>
          {errors.cognomeNome && <p className="text-red-500 text-sm mt-1">{errors.cognomeNome}</p>}
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Indirizzo Residenza Intestatario al PRA <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="indirizzoResidenza"
            value={formData.indirizzoResidenza}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.indirizzoResidenza ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent transition-all`}
            placeholder="Via Roma 1, 00100 Roma (RM)"
          />
          {errors.indirizzoResidenza && <p className="text-red-500 text-sm mt-1">{errors.indirizzoResidenza}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Codice Fiscale <span className="text-gray-400 text-xs">(opzionale)</span>
          </label>
          <input
            type="text"
            name="codiceFiscale"
            value={formData.codiceFiscale}
            onChange={handleInputChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent transition-all uppercase"
            placeholder="RSSMRA80A01H501U"
            maxLength={16}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Professione <span className="text-gray-400 text-xs">(opzionale)</span>
          </label>
          <input
            type="text"
            name="professione"
            value={formData.professione}
            onChange={handleInputChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent transition-all"
            placeholder="Es. Impiegato, Libero professionista..."
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Titolo di Studio <span className="text-gray-400 text-xs">(opzionale)</span>
          </label>
          <input
            type="text"
            name="titoloStudio"
            value={formData.titoloStudio}
            onChange={handleInputChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent transition-all"
            placeholder="Es. Diploma, Laurea..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#5FB76E]/10 rounded-lg flex items-center justify-center">
          <Shield className="text-[#5FB76E]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#5FB76E]">Garanzie e Coperture</h3>
      </div>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Massimale RCA <span className="text-red-500">*</span>
          </label>
          <select
            name="massimaleRCA"
            value={formData.massimaleRCA}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.massimaleRCA ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#5FB76E] focus:border-transparent transition-all bg-white`}
          >
            <option value="">Seleziona massimale...</option>
            {massimaliOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {errors.massimaleRCA && <p className="text-red-500 text-sm mt-1">{errors.massimaleRCA}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Garanzie Accessorie
          </label>
          <div className="grid sm:grid-cols-2 gap-3">
            {garanzieOptions.map(garanzia => (
              <label 
                key={garanzia} 
                className={`flex items-start space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.garanzie.includes(garanzia)
                    ? 'border-[#5FB76E] bg-[#5FB76E]/5'
                    : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  name="garanzie"
                  value={garanzia}
                  checked={formData.garanzie.includes(garanzia)}
                  onChange={handleInputChange}
                  className="mt-1 w-4 h-4 text-[#5FB76E] rounded focus:ring-[#5FB76E]"
                />
                <span className="text-sm text-gray-700">{garanzia}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#E67E22]/10 rounded-lg flex items-center justify-center">
          <Mail className="text-[#E67E22]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#E67E22]">Contatti e Dichiarazioni</h3>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            E-mail <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#E67E22] focus:border-transparent transition-all`}
              placeholder="email@esempio.it"
            />
          </div>
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cellulare <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="tel"
              name="cellulare"
              value={formData.cellulare}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.cellulare ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#E67E22] focus:border-transparent transition-all`}
              placeholder="+39 333 000 0000"
            />
          </div>
          {errors.cellulare && <p className="text-red-500 text-sm mt-1">{errors.cellulare}</p>}
        </div>
      </div>
      
      {/* Consenso Privacy Obbligatorio */}
      <div className={`p-5 rounded-xl border-2 ${errors.consensoPrivacy ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'}`}>
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            name="consensoPrivacy"
            checked={formData.consensoPrivacy}
            onChange={handleInputChange}
            className="mt-1 w-5 h-5 text-[#E67E22] rounded focus:ring-[#E67E22]"
          />
          <span className="text-sm text-gray-700 leading-relaxed">
            Dichiaro di aver preso visione dell'<a href="/privacy-policy" className="text-[#2B7A9F] underline" target="_blank">Informativa Privacy</a> e, ai sensi della normativa vigente sulla vendita a distanza, autorizzo espressamente l'uso di tecniche di comunicazione a distanza. Acconsento inoltre a ricevere tutta la documentazione precontrattuale (Dip, Dip Aggiuntivo) e contrattuale esclusivamente in formato elettronico all'indirizzo email sopra indicato. <span className="text-red-500">*</span>
          </span>
        </label>
        {errors.consensoPrivacy && <p className="text-red-500 text-sm mt-2">{errors.consensoPrivacy}</p>}
      </div>
      
      {/* Consenso Marketing Facoltativo */}
      <div className="p-5 rounded-xl border-2 border-gray-200 bg-white">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            name="consensoMarketing"
            checked={formData.consensoMarketing}
            onChange={handleInputChange}
            className="mt-1 w-5 h-5 text-[#E67E22] rounded focus:ring-[#E67E22]"
          />
          <span className="text-sm text-gray-700">
            Acconsento al trattamento dei dati per finalita di promozione commerciale.
          </span>
        </label>
      </div>
      
      {/* Nota GDPR */}
      <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500 leading-relaxed">
        I tuoi dati sono al sicuro. Il Titolare del trattamento e Assietica Soc. Coop. Societa Benefit. Trattiamo i tuoi dati esclusivamente per fornirti la quotazione assicurativa richiesta nel pieno rispetto del Regolamento UE 2016/679 (GDPR). Puoi revocare il tuo consenso in qualsiasi momento scrivendo a <a href="mailto:agenzia@assietica.it" className="text-[#2B7A9F]">agenzia@assietica.it</a>. Agenzia iscritta al RUI - Vigilanza IVASS.
      </div>
      
      {/* Nota Precontrattuale */}
      <div className="bg-[#2B7A9F]/5 border border-[#2B7A9F]/10 rounded-lg p-4 text-xs text-gray-600 leading-relaxed">
        <strong>Nessun vincolo contrattuale:</strong> l'invio della presente richiesta ha finalità esclusivamente di preventivo e non ti impegna in alcun modo all'acquisto. Prima dell'eventuale sottoscrizione riceverai gratuitamente tutta la <a href="/precontrattuale" target="_blank" rel="noopener noreferrer" className="text-[#2B7A9F] underline">documentazione precontrattuale</a> prevista dalla normativa IVASS (Allegati, DIP e DIP Aggiuntivo).
      </div>
    </div>
  );

  if (submitStatus === 'success') {
    return (
      <div className="text-center py-12 animate-fade-in">
        <div className="w-20 h-20 bg-[#5FB76E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="text-[#5FB76E]" size={40} />
        </div>
        <h3 className="text-2xl font-bold text-[#5FB76E] mb-4">Richiesta Inviata!</h3>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          Ti ringraziamo per la richiesta! I nostri consulenti la stanno prendendo in carico e riceverai il tuo preventivo personalizzato al piu presto.
        </p>
        <button
          onClick={() => {
            setSubmitStatus(null);
            setCurrentStep(1);
            setFormData({
              qualificazione: '', targa: '', cognomeNome: '', indirizzoResidenza: '',
              veicoloEstero: 'no', codiceFiscale: '', professione: '', titoloStudio: '',
              massimaleRCA: '', garanzie: [], email: '', cellulare: '',
              consensoPrivacy: false, consensoMarketing: false
            });
          }}
          className="bg-[#E74C3C] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#E74C3C]/90 transition-all"
        >
          Nuova Richiesta
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {renderStepIndicator()}
      
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          
          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="text-red-500" size={20} />
              <span className="text-red-700">Si e verificato un errore. Riprova piu tardi.</span>
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                currentStep === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft size={20} />
              <span>Indietro</span>
            </button>
            
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-[#E74C3C] to-[#E67E22] text-white hover:shadow-lg hover:scale-105 transition-all"
              >
                <span>Avanti</span>
                <ChevronRight size={20} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center space-x-2 px-8 py-3 rounded-lg font-semibold bg-gradient-to-r from-[#5FB76E] to-[#3AAFA9] text-white hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Invio in corso...</span>
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    <span>Richiedi Quotazione</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </form>
      
      {/* Social Proof */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center space-x-2 bg-[#5FB76E]/10 text-[#5FB76E] px-6 py-3 rounded-full">
          <CheckCircle size={20} />
          <span className="font-medium text-sm">I soci o dipendenti di Cooperative o ETS hanno gia risparmiato sulla loro RCA e sulle loro coperture personali</span>
        </div>
      </div>
    </div>
  );
};

export default BastaLaTargaForm;
